"use client"

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react'
import { useFormContext, FieldValues } from 'react-hook-form'

type DirtyFieldsContextType = {
    isFieldDirty: (fieldName: string) => boolean
    isFormClean: boolean
}

const DirtyFieldsContext = createContext<DirtyFieldsContextType | undefined>(undefined)

export function useDirtyFields() {
    const context = useContext(DirtyFieldsContext)
    if (context === undefined) {
        // Return a fallback context when not within a DirtyFieldsProvider
        return {
            isFieldDirty: () => false,
            isFormClean: false
        }
    }
    return context
}

type DirtyFieldsProviderProps = {
    children: React.ReactNode
    isFormClean: boolean
}

export function DirtyFieldsProvider<TFieldValues extends FieldValues = FieldValues>({ 
    children, 
    isFormClean 
}: DirtyFieldsProviderProps) {
    const { formState: { dirtyFields, isDirty } } = useFormContext<TFieldValues>()
    const [forceUpdate, setForceUpdate] = useState(0)
    
    // Force re-render when dirty fields change
    useEffect(() => {
        setForceUpdate(prev => prev + 1)
    }, [dirtyFields, isDirty])
    
    const isFieldDirty = useMemo(() => {
        return (fieldName: string): boolean => {
            if (isFormClean) return false
            
            // Check if the field is in the dirtyFields object
            const fieldParts = fieldName.split('.')
            let current: any = dirtyFields
            
            for (const part of fieldParts) {
                if (current && typeof current === 'object' && part in current) {
                    current = current[part]
                } else {
                    return false
                }
            }
            
            return current === true
        }
    }, [dirtyFields, isFormClean])
    
    const contextValue = useMemo(() => ({
        isFieldDirty,
        isFormClean
    }), [isFieldDirty, isFormClean])
    
    return (
        <DirtyFieldsContext.Provider value={contextValue}>
            {children}
        </DirtyFieldsContext.Provider>
    )
}


